import { useState, memo, useCallback } from "react";
import {
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

/**
 * @typedef {object} BodyNode
 * @property {string} id - Single node identifier
 * @property {string} key - JSON key
 * @property {string} type - data type ("string", "number", "boolean", "object", "array")
 * @property {any} value - value
 * @property {Array<BodyNode>} children - Node Child (if type is "object" or "array")
 */

/**
 * Recursive sub-component to render the nested fields of the Body
 * * @param {object} props
 * @param {BodyNode} props.node - Actual node.
 * @param {number} props.depth - Visual depth level
 * @param {Function} props.onChange - Update Handler
 * @param {Function} props.onDelete - Delete Handler
 * @param {Function} props.onAddChild - Handler to add a child (object type only)
 * @param {boolean} props.isParentArray - Indicates whether the parent node is an array
 * @returns {JSX.Element}
 */

const NestedFormRow = ({
  node,
  depth,
  onChange,
  onDelete,
  onAddChild,
  isParentArray = false,
}) => {
  const isContainer = node.type === "object" || node.type === "array";
  return (
    <Box sx={{ ml: depth * 3, mt: 1, p: 1, borderLeft: "2px solid #e0e0e0" }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={3}>
          <TextField
            fullWidth
            size="small"
            label={isParentArray ? "Índice (Automático)" : "Llave"}
            value={isParentArray ? node.key || "-" : node.key}
            onChange={(e) => onChange(node.id, "key", e.target.value)}
            disabled={isParentArray}
            sx={
              isParentArray
                ? { backgroundColor: "#f5f5f5", borderRadius: 1 }
                : {}
            }
          />
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              value={node.type}
              label="Tipo"
              onChange={(e) => onChange(node.id, "type", e.target.value)}
            >
              <MenuItem value="string">Cadena de Texto</MenuItem>
              <MenuItem value="number">Numero</MenuItem>
              <MenuItem value="boolean">Lógico (Si/No)</MenuItem>
              <MenuItem value="array">Agregear Listado</MenuItem>
              <MenuItem value="object">Agregar Propiedad</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {!isContainer ? (
          <Grid item xs={4}>
            {node.type === "boolean" ? (
              <FormControl fullWidth size="small">
                <Select
                  value={node.value}
                  onChange={(e) => onChange(node.id, "value", e.target.value)}
                >
                  <MenuItem value="true">Sí</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Valor"
                value={node.value}
                type={node.type === "number" ? "number" : "text"}
                onChange={(e) => onChange(node.id, "value", e.target.value)}
              />
            )}
          </Grid>
        ) : (
          <Grid item xs={4}>
            <Button
              sx={{ marginTop: "30px" }}
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onAddChild(node.id)}
            >
              {node.type === "array" ? "Agregar Ítem" : "Agregar Propiedad"}
            </Button>
          </Grid>
        )}

        <Grid item xs={2}>
          <IconButton
            sx={{ marginTop: "30px" }}
            size="small"
            color="error"
            onClick={() => onDelete(node.id)}
          >
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>

      {isContainer &&
        node.children &&
        node.children.map((child) => (
          <NestedFormRow
            key={child.id}
            node={child}
            depth={depth + 1}
            onChange={onChange}
            onDelete={onDelete}
            onAddChild={onAddChild}
            isParentArray={node.type === "array"}
          />
        ))}
    </Box>
  );
};

export default memo(NestedFormRow);
